import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Filter, Calendar, Eye, RefreshCw, Ticket, Plane, Archive, CheckCircle, Undo2, Clock, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { format } from "date-fns";
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';
import EmptyState from '../components/EmptyState';
import StatusBadge from '../components/StatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Booking classification ─────────────────────────────────────────────────

function classifyBooking(booking) {
  if (booking.lifecycle_group) return booking.lifecycle_group;
  const st = (booking.status || "").toLowerCase();
  if (st === "cancelled" || st === "refunded") return "cancelled";
  const ls = (booking.lifecycle_status || "").toLowerCase();
  if (ls === "cancelled") return "cancelled";
  if (booking.manual_past_travel || ls === "trip_completed" || ls === "closed") return "past";
  const lsd = booking.last_segment_date || booking.expiry_date;
  if (lsd) {
    const d = new Date(lsd + "T00:00:00");
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return "past";
  }
  return "active";
}

// ── Travel period filter ───────────────────────────────────────────────────

function getWeekRange() {
  const d = new Date();
  const day = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - ((day + 6) % 7));
  mon.setHours(0, 0, 0, 0);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return [mon, sun];
}

function matchesPeriod(booking, period) {
  if (period === "all") return true;
  const lsd = booking.last_segment_date || booking.expiry_date;
  if (!lsd) return false;
  const d = new Date(lsd + "T00:00:00");
  const now = new Date();
  if (period === "this_year")    return d.getFullYear() === now.getFullYear();
  if (period === "this_quarter") {
    const q = Math.floor(now.getMonth() / 3);
    return d.getFullYear() === now.getFullYear() && Math.floor(d.getMonth() / 3) === q;
  }
  if (period === "this_month")   return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  if (period === "this_week") {
    const [mon, sun] = getWeekRange();
    return d >= mon && d <= sun;
  }
  return true;
}

// ── Sort ───────────────────────────────────────────────────────────────────

function applySort(rows, key, dir) {
  if (!key) return rows;
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (key === "airline") {
      const aa = (a.airline || "").toLowerCase();
      const bb = (b.airline || "").toLowerCase();
      // Entries without airline always sort last regardless of direction
      if (!a.airline && !b.airline) return 0;
      if (!a.airline) return 1;
      if (!b.airline) return -1;
      return aa < bb ? -sign : aa > bb ? sign : 0;
    }
    if (key === "booking_date") {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return (ta - tb) * sign;
    }
    if (key === "upcoming_travel") {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const sortVal = (x) => {
        const fd = x.first_departure_date;
        if (!fd) return [2, 0]; // undated → always last
        const ts = new Date(fd + "T00:00:00").getTime();
        if (ts < today.getTime()) return [1, ts]; // past departure → second group
        return [0, ts]; // upcoming → first group
      };
      const [ga, va] = sortVal(a);
      const [gb, vb] = sortVal(b);
      if (ga !== gb) return ga - gb; // group order unaffected by direction
      return (va - vb) * sign;
    }
    if (key === "expiry_date") {
      const da = a.last_segment_date || a.expiry_date;
      const db = b.last_segment_date || b.expiry_date;
      // Undated always last
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      const ta = new Date(da + "T00:00:00").getTime();
      const tb = new Date(db + "T00:00:00").getTime();
      return (ta - tb) * sign;
    }
    return 0;
  });
}

// ── Expiry badge ───────────────────────────────────────────────────────────

function ExpiryInfo({ booking }) {
  const lsd = booking.last_segment_date || booking.expiry_date;
  if (!lsd) return null;

  const days = booking.days_to_expiry != null
    ? booking.days_to_expiry
    : (() => {
        const d  = new Date(lsd + "T00:00:00");
        const t  = new Date(); t.setHours(0, 0, 0, 0);
        return Math.round((d - t) / 86400000);
      })();

  const dateLabel = (() => {
    try { return format(new Date(lsd + "T00:00:00"), "d MMM yyyy"); }
    catch { return lsd; }
  })();

  let countdown, countdownClass;
  if (days > 0) {
    countdown = `${days} day${days !== 1 ? "s" : ""} remaining`;
    countdownClass = days <= 7 ? "text-amber-600 font-semibold" : "text-slate-500";
  } else if (days === 0) {
    countdown = "Expires today";
    countdownClass = "text-amber-600 font-semibold";
  } else {
    countdown = `Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} ago`;
    countdownClass = "text-slate-400";
  }

  return (
    <div className="text-xs">
      <div className="flex items-center gap-1 text-slate-600">
        <Clock className="w-3 h-3 flex-shrink-0" />
        <span>{dateLabel}</span>
      </div>
      <div className={`mt-0.5 ${countdownClass}`}>{countdown}</div>
    </div>
  );
}

export default function Bookings() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [travelPeriod, setTravelPeriod] = useState("all");
  const [airlineFilter, setAirlineFilter] = useState("all");
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [showCancelled, setShowCancelled] = useState(false);
  const [movingId, setMovingId] = useState(null);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings`, { headers: getAuthHeaders() });
      setBookings(response.data);
    } catch (error) {
      console.error("Failed to fetch bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, []); // eslint-disable-line
  useEffect(() => { if (statusFilter === "cancelled") setShowCancelled(true); }, [statusFilter]);

  const moveBooking = async (e, booking, toPast) => {
    e.stopPropagation();
    setMovingId(booking.id);
    try {
      const now = new Date().toISOString();
      const payload = toPast
        ? {
            manual_past_travel: true,
            manual_past_travel_at: now,
            manual_past_travel_reason: "Manually moved because trip was already completed but stored travel dates were missing or incorrect.",
          }
        : {
            manual_past_travel: false,
            manual_past_travel_at: null,
            manual_past_travel_reason: null,
          };
      await axios.patch(`${API}/bookings/${booking.id}`, payload, { headers: getAuthHeaders() });
      setBookings(prev => prev.map(b => b.id === booking.id
        ? { ...b, ...payload, lifecycle_group: toPast ? "past" : "active" }
        : b));
    } catch (err) {
      console.error("Failed to update booking:", err);
    } finally {
      setMovingId(null);
    }
  };

  const STATUS_LABELS = {
    confirmed:           "Confirmed",
    pending_ticketing:   "Pending Ticketing",
    ticketed:            "Ticketed",
    completed:           "Completed",
    cancelled:           "Cancelled",
    refunded:            "Refunded",
    partially_cancelled: "Partial Cancel",
  };

  // Unique sorted airline list for filter dropdown
  const uniqueAirlines = useMemo(() => {
    const names = new Set();
    bookings.forEach(b => { if (b.airline) names.add(b.airline); });
    return Array.from(names).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [bookings]);

  const matchesSearch = (booking) => {
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    return (
      booking.pnr?.toLowerCase().includes(q) ||
      (booking.passengers || []).some(p =>
        p.first_name?.toLowerCase().includes(q) ||
        p.last_name?.toLowerCase().includes(q)
      )
    );
  };

  const matchesFilter = (booking) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "cancelled") return classifyBooking(booking) === "cancelled";
    if (statusFilter === "completed") return classifyBooking(booking) === "past";
    return booking.status === statusFilter;
  };

  const matchesAirline = (booking) => {
    if (airlineFilter === "all") return true;
    return (booking.airline || "") === airlineFilter;
  };

  const filtered = bookings.filter(b =>
    matchesSearch(b) && matchesFilter(b) && matchesPeriod(b, travelPeriod) && matchesAirline(b)
  );

  const activeBookings    = applySort(filtered.filter(b => classifyBooking(b) === "active"), sortKey, sortDir);
  const pastBookings      = applySort(filtered.filter(b => classifyBooking(b) === "past"), sortKey, sortDir);
  const cancelledBookings = applySort(
    bookings.filter(b => classifyBooking(b) === "cancelled" && matchesSearch(b) && matchesFilter(b) && matchesAirline(b)),
    sortKey, sortDir
  );

  const toggleSortDir = () => setSortDir(d => d === "asc" ? "desc" : "asc");

  const handleSortKey = (key) => {
    if (key === sortKey) {
      toggleSortDir();
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ colKey }) => {
    if (sortKey !== colKey) return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300 inline" />;
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 text-slate-600 inline" />
      : <ArrowDown className="w-3 h-3 ml-1 text-slate-600 inline" />;
  };

  const BookingRow = ({ booking }) => (
    <TableRow
      key={booking.id}
      className="table-row-hover cursor-pointer"
      onClick={() => navigate(`/bookings/${booking.id}`)}
      data-testid={`booking-row-${booking.id}`}
    >
      <TableCell>
        <span className="font-mono font-bold text-slate-900">{booking.pnr}</span>
      </TableCell>
      <TableCell>
        <div>
          {(booking.passengers || []).map((p, i) => (
            <span key={i} className="block text-sm">
              {p.title} {p.first_name} {p.last_name}
            </span>
          ))}
        </div>
      </TableCell>
      <TableCell data-testid={`airline-cell-${booking.id}`}>
        <span className="text-sm text-slate-700">{booking.airline || <span className="text-slate-400">—</span>}</span>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="capitalize">{booking.offer_type}</Badge>
      </TableCell>
      <TableCell>
        <span className="font-mono">€{(booking.base_price ?? 0).toFixed(2)}</span>
      </TableCell>
      <TableCell>
        <span className="font-mono">€{(booking.selling_price ?? 0).toFixed(2)}</span>
      </TableCell>
      <TableCell>
        <span className={`font-mono font-bold ${(booking.profit ?? 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
          €{(booking.profit ?? 0).toFixed(2)}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <StatusBadge status={booking.status} label={STATUS_LABELS[booking.status] || booking.status} />
          {booking.manual_past_travel && (
            <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Manually marked past travel</Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <ExpiryInfo booking={booking} />
      </TableCell>
      <TableCell>
        <span className="text-sm text-slate-500">
          {booking.created_at ? format(new Date(booking.created_at), "MMM d, yyyy") : "—"}
        </span>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost"
            onClick={e => { e.stopPropagation(); navigate(`/bookings/${booking.id}`); }}
            data-testid={`view-booking-${booking.id}-btn`}>
            <Eye className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost"
            onClick={e => { e.stopPropagation(); navigate(`/tickets/${booking.id}`); }}
            data-testid={`generate-ticket-${booking.id}-btn`}>
            <Ticket className="w-4 h-4" />
          </Button>
          {!booking.manual_past_travel && classifyBooking(booking) === "active" && (
            <Button size="sm" variant="ghost" title="Move to Past Travel"
              disabled={movingId === booking.id}
              onClick={e => moveBooking(e, booking, true)}
              data-testid={`move-past-${booking.id}-btn`}>
              <CheckCircle className="w-4 h-4 text-slate-400" />
            </Button>
          )}
          {booking.manual_past_travel && (
            <Button size="sm" variant="ghost" title="Move back to Active"
              disabled={movingId === booking.id}
              onClick={e => moveBooking(e, booking, false)}
              data-testid={`undo-past-${booking.id}-btn`}>
              <Undo2 className="w-4 h-4 text-indigo-500" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  const SectionTable = ({ rows, emptyMsg }) => (
    rows.length === 0 ? (
      <EmptyState title={emptyMsg || "No bookings"} description="No bookings match the current filter." />
    ) : (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>PNR</TableHead>
            <TableHead>Passengers</TableHead>
            <TableHead
              className="cursor-pointer select-none whitespace-nowrap"
              onClick={() => handleSortKey("airline")}
              data-testid="sort-airline-header"
            >
              Airline <SortIcon colKey="airline" />
            </TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Base Price</TableHead>
            <TableHead>Selling Price</TableHead>
            <TableHead>Profit</TableHead>
            <TableHead>Status</TableHead>
            <TableHead
              className="cursor-pointer select-none whitespace-nowrap"
              onClick={() => handleSortKey("expiry_date")}
              data-testid="sort-expiry-header"
            >
              Expiry / Last Flight <SortIcon colKey="expiry_date" />
            </TableHead>
            <TableHead
              className="cursor-pointer select-none whitespace-nowrap"
              onClick={() => handleSortKey("booking_date")}
              data-testid="sort-booking-date-header"
            >
              Created <SortIcon colKey="booking_date" />
            </TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(b => <BookingRow key={b.id} booking={b} />)}
        </TableBody>
      </Table>
    )
  );

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="bookings-page">
        {/* Header */}
        <PageHeader
          title="Bookings"
          subtitle="Manage confirmed bookings and generate tickets"
          actions={
            <Button onClick={fetchBookings} variant="outline" data-testid="refresh-bookings-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          }
        />

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by PNR or passenger name..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-bookings-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter-select">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending_ticketing">Pending Ticketing</SelectItem>
                  <SelectItem value="ticketed">Ticketed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              <Select value={travelPeriod} onValueChange={setTravelPeriod}>
                <SelectTrigger className="w-[200px]" data-testid="period-filter-select">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Travel period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Periods</SelectItem>
                  <SelectItem value="this_week">This Week</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="this_quarter">This Quarter</SelectItem>
                  <SelectItem value="this_year">This Year</SelectItem>
                </SelectContent>
              </Select>
              <Select value={airlineFilter} onValueChange={setAirlineFilter}>
                <SelectTrigger className="w-[200px]" data-testid="airline-filter-select">
                  <Plane className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by airline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Airlines</SelectItem>
                  {uniqueAirlines.map(a => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortKey || "none"} onValueChange={v => { setSortKey(v === "none" ? "" : v); setSortDir("asc"); }}>
                <SelectTrigger className="w-[210px]" data-testid="sort-key-select">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Default order</SelectItem>
                  <SelectItem value="airline">Airline</SelectItem>
                  <SelectItem value="booking_date">Booking Date</SelectItem>
                  <SelectItem value="upcoming_travel">Upcoming Travel</SelectItem>
                  <SelectItem value="expiry_date">Expiry Date</SelectItem>
                </SelectContent>
              </Select>
              {sortKey && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSortDir}
                  title={sortDir === "asc" ? "Currently ascending — click for descending" : "Currently descending — click for ascending"}
                  data-testid="sort-direction-toggle"
                  className="h-10 px-3"
                >
                  {sortDir === "asc"
                    ? <><ArrowUp className="w-4 h-4 mr-1" />Asc</>
                    : <><ArrowDown className="w-4 h-4 mr-1" />Desc</>
                  }
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <PageLoader message="Loading bookings…" />
        ) : (
          <div className="space-y-8">
            {/* Active / Upcoming */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Plane className="w-4 h-4 text-blue-500" />
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Active / Upcoming Bookings
                </h2>
                <span className="text-xs text-gray-400 font-normal">({activeBookings.length})</span>
              </div>
              <Card data-testid="active-bookings-card">
                <CardContent className="p-0">
                  <SectionTable rows={activeBookings} emptyMsg="No active or upcoming bookings" />
                </CardContent>
              </Card>
            </div>

            {/* Past Travel / Completed Trips */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Archive className="w-4 h-4 text-slate-400" />
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Past Travel / Completed Trips
                </h2>
                <span className="text-xs text-gray-400 font-normal">({pastBookings.length})</span>
              </div>
              <Card data-testid="past-bookings-card">
                <CardContent className="p-0">
                  <SectionTable rows={pastBookings} emptyMsg="No completed trips yet" />
                </CardContent>
              </Card>
            </div>

            {/* Cancelled / Refunded (collapsed by default) */}
            {cancelledBookings.length > 0 && (
              <div>
                <button
                  className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-600 mb-3"
                  onClick={() => setShowCancelled(v => !v)}
                >
                  <span>{showCancelled ? "▾" : "▸"}</span>
                  <span className="uppercase tracking-wide font-semibold">
                    Cancelled / Refunded ({cancelledBookings.length})
                  </span>
                </button>
                {showCancelled && (
                  <Card>
                    <CardContent className="p-0">
                      <SectionTable rows={cancelledBookings} emptyMsg="" />
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
